import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, ArrowLeftRight, CheckCircle, HelpCircle,
  Globe, Zap, Shield, Users, Sparkles
} from "lucide-react";

const changes = [
  {
    old: "Static website with limited interactivity",
    new: "Modern, responsive web application with real-time features",
    icon: Zap,
  },
  {
    old: "Manual contact form submissions",
    new: "AI-powered chat assistant available 24/7",
    icon: Sparkles,
  },
  {
    old: "Basic information pages",
    new: "Comprehensive service pages with clear conversion paths",
    icon: Globe,
  },
  {
    old: "Regional focus",
    new: "Global network showcase spanning 50+ countries",
    icon: Users,
  },
  {
    old: "Desktop-first design",
    new: "Mobile-first, accessible on any device",
    icon: Shield,
  },
];

const faqs = [
  {
    question: "Why did M2L update its website?",
    answer: "To better serve our global community with modern technology, improved user experience, and enhanced digital services. Our mission remains the same — only our tools have evolved.",
  },
  {
    question: "Is the old website still accessible?",
    answer: "Legacy content is preserved in our Heritage Archive. You can explore our history, photos, and past programs at /heritage.",
  },
  {
    question: "Have your services changed?",
    answer: "Our core services remain the same: student support, academic programs, and professional development. We've expanded our offerings and improved how you access them.",
  },
  {
    question: "How do I find information from the old site?",
    answer: "Use the content mapping below to find where old content now lives. Most pages have been updated and enhanced rather than removed.",
  },
  {
    question: "Can I still contact the team the same way?",
    answer: "Yes! All contact methods remain available. Plus, you can now use our AI assistant for instant help 24/7.",
  },
];

const contentMap = [
  { old: "Home", new: "Home", oldPath: "/", newPath: "/" },
  { old: "About Us", new: "About", oldPath: "/about", newPath: "/about" },
  { old: "Students", new: "Services", oldPath: "/students", newPath: "/services" },
  { old: "Services", new: "Services", oldPath: "/#services", newPath: "/services" },
  { old: "Contact", new: "Contact", oldPath: "/#contact", newPath: "/contact" },
  { old: "Testimonials", new: "Heritage Archive", oldPath: "/about#testimonials", newPath: "/heritage/archive" },
  { old: "Gallery", new: "Heritage Archive", oldPath: "/students#gallery", newPath: "/heritage/archive" },
  { old: "Our Team", new: "Heritage", oldPath: "/about#team", newPath: "/heritage" },
];

const TransitionPage = () => {
  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-[#060B19] to-[#060B19]" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[200px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-[150px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-3 mb-6 justify-center">
              <ArrowLeftRight className="w-6 h-6 text-primary" />
              <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase">
                Website Transition
              </span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Welcome to the{" "}
              <span className="gradient-text">Next Chapter</span>
            </h1>

            <p className="text-xl text-white/60 leading-relaxed mb-8">
              Our legacy continues with a new digital experience designed for 
              today's global citizens. Here's what changed and where to find everything.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-[#060B19] rounded-lg font-semibold hover:bg-primary-hover transition-all group"
              >
                Explore New Site
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/heritage"
                className="inline-flex items-center gap-2 px-6 py-3 border border-secondary/30 text-secondary rounded-lg font-medium hover:bg-secondary/10 transition-all"
              >
                Visit Heritage Archive
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Changed */}
      <section className="py-20 bg-surface-solid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              What Changed
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              A side-by-side look at our evolution from legacy to modern experience.
            </p>
          </motion.div>

          <div className="space-y-6">
            {changes.map((change, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-xl p-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-[1fr,auto,1fr] gap-6 items-center">
                  {/* Old */}
                  <div className="text-center md:text-right">
                    <span className="text-xs text-white/40 uppercase tracking-wider mb-2 block">Before</span>
                    <p className="text-white/60">{change.old}</p>
                  </div>

                  {/* Arrow */}
                  <div className="flex justify-center">
                    <div className="w-12 h-12 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center">
                      <change.icon className="w-6 h-6 text-primary" />
                    </div>
                  </div>

                  {/* New */}
                  <div className="text-center md:text-left">
                    <span className="text-xs text-primary uppercase tracking-wider mb-2 block">After</span>
                    <p className="text-white font-medium">{change.new}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Mapping */}
      <section className="py-20 bg-[#060B19]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Where to Find Things
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Use this map to find where content from the legacy site now lives.
            </p>
          </motion.div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-white/40 font-medium">Old Page</th>
                  <th className="text-center py-4 px-4 text-white/40 font-medium"></th>
                  <th className="text-left py-4 px-4 text-white/40 font-medium">New Location</th>
                  <th className="text-right py-4 px-4 text-white/40 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {contentMap.map((item, idx) => (
                  <motion.tr
                    key={idx}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.05 }}
                    className="border-b border-white/5 hover:bg-white/5"
                  >
                    <td className="py-4 px-4">
                      <span className="text-white/60">{item.old}</span>
                      <span className="text-white/30 text-sm block">{item.oldPath}</span>
                    </td>
                    <td className="py-4 px-4 text-center">
                      <ArrowRight className="w-4 h-4 text-primary mx-auto" />
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-white font-medium">{item.new}</span>
                      <span className="text-primary text-sm block">{item.newPath}</span>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <Link
                        to={item.newPath}
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        Visit
                        <ArrowRight className="w-3 h-3" />
                      </Link>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-surface-solid">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <HelpCircle className="w-10 h-10 text-primary mx-auto mb-4" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-xl p-6"
              >
                <h3 className="font-semibold text-white mb-3 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  {faq.question}
                </h3>
                <p className="text-white/60 ml-8">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-[#060B19] to-secondary/10 border-y border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Continue Your Journey?
            </h2>
            <p className="text-white/60 mb-8">
              Whether you're a new visitor or returning alumni, we're here to help 
              you achieve your educational goals.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-[#060B19] rounded-lg font-semibold hover:bg-primary-hover transition-all"
              >
                Explore Services
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white rounded-lg font-medium hover:bg-white/10 transition-all"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default TransitionPage;
